import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import FastForwardOutlinedIcon from '@mui/icons-material/FastForwardOutlined';
import FastRewindOutlinedIcon from '@mui/icons-material/FastRewindOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { debugLog } from '../../utils/debugLog';
import {
  PICTURE_FRAME_MS,
  PictureAngle,
  PictureLineUpPlan,
  describeStart,
  formatMoment,
  openingMoments,
  planPictureLineUp,
} from './lineUpByPicture';

export interface PictureSource {
  /** "Master (angle A)", "Close-up (angle B)". */
  name: string;
  url: string;
}

interface LineUpByPictureDialogProps {
  master: PictureSource & { sourceInMs: number; durationMs: number };
  angle: PictureSource & PictureAngle;
  onCancel: () => void;
  onLineUp: (plan: PictureLineUpPlan) => void;
}

interface PicturePlayerProps {
  label: string;
  url: string;
  durationMs: number;
  positionMs: number;
  onPositionChange: (ms: number) => void;
  testId: string;
}

/** One file, paused on a frame: scrub, step a frame or a second, or play to find the moment. */
const PicturePlayer: React.FC<PicturePlayerProps> = ({ label, url, durationMs, positionMs, onPositionChange, testId }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [lengthMs, setLengthMs] = useState(durationMs);
  // The first position, put on the picture once the file can seek.
  const openingRef = useRef(positionMs);

  // Nothing keeps playing once the dialog closes.
  useEffect(() => {
    const el = videoRef.current;
    return () => {
      if (el && !el.paused) el.pause();
    };
  }, []);

  const seekTo = (ms: number) => {
    const clamped = Math.max(0, lengthMs > 0 ? Math.min(ms, lengthMs) : ms);
    const el = videoRef.current;
    if (el) {
      if (!el.paused) el.pause();
      try {
        el.currentTime = clamped / 1000;
      } catch {
        // Not seekable before its metadata loads; onLoadedMetadata puts it there.
      }
    }
    onPositionChange(clamped);
  };

  const toggle = () => {
    const el = videoRef.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    const started = el.play() as Promise<void> | undefined;
    if (started && typeof started.catch === 'function') {
      started.catch((error: unknown) => debugLog('Line up by picture: could not play', error));
    }
  };

  const step = (deltaMs: number) => seekTo(positionMs + deltaMs);

  return (
    <Box role="group" aria-label={label} data-testid={testId} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }} noWrap title={label}>
        {label}
      </Typography>
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: 'common.black', borderRadius: 2, overflow: 'hidden' }}>
        {/* Creator uploads have no captions; the player is for finding a visible moment. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          src={url}
          preload="auto"
          playsInline
          aria-label={label}
          data-testid={`${testId}-video`}
          onLoadedMetadata={(event) => {
            const el = event.currentTarget;
            if (Number.isFinite(el.duration) && el.duration > 0) setLengthMs(Math.round(el.duration * 1000));
            try {
              el.currentTime = openingRef.current / 1000;
            } catch {
              /* stays at the start */
            }
          }}
          onPlay={() => setPlaying(true)}
          onPause={(event) => {
            setPlaying(false);
            onPositionChange(Math.round(event.currentTarget.currentTime * 1000));
          }}
          onTimeUpdate={(event) => {
            if (!event.currentTarget.paused) onPositionChange(Math.round(event.currentTarget.currentTime * 1000));
          }}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </Box>
      <Slider
        aria-label={`Position in ${label}`}
        size="small"
        min={0}
        max={Math.max(lengthMs, 1)}
        step={PICTURE_FRAME_MS}
        value={Math.min(positionMs, Math.max(lengthMs, 1))}
        onChange={(_, value) => seekTo(value as number)}
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => formatMoment(value)}
        sx={{ mt: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
        <Tooltip title="Back 1 second">
          <IconButton size="small" aria-label="Back 1 second" onClick={() => step(-1000)}>
            <FastRewindOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Back 1 frame">
          <IconButton size="small" aria-label="Back 1 frame" onClick={() => step(-PICTURE_FRAME_MS)}>
            <ChevronLeftOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" aria-label={playing ? 'Pause' : 'Play'} onClick={toggle}>
          {playing ? <PauseOutlinedIcon fontSize="small" /> : <PlayArrowOutlinedIcon fontSize="small" />}
        </IconButton>
        <Tooltip title="Forward 1 frame">
          <IconButton size="small" aria-label="Forward 1 frame" onClick={() => step(PICTURE_FRAME_MS)}>
            <ChevronRightOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Forward 1 second">
          <IconButton size="small" aria-label="Forward 1 second" onClick={() => step(1000)}>
            <FastForwardOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="body2"
          data-testid={`${testId}-time`}
          sx={{ ml: 1, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMoment(positionMs)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Lines one angle up with the master by a moment both can see, for angles
 * whose sound doesn't match (or has none). Opens with both players where the
 * angle is lined up now, so an angle already in step shows the same picture.
 */
const LineUpByPictureDialog: React.FC<LineUpByPictureDialogProps> = ({ master, angle, onCancel, onLineUp }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [moments, setMoments] = useState(() => openingMoments(master, angle));
  const plan = planPictureLineUp(moments.masterMs, moments.angleMs, master.sourceInMs, angle);

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="lg" fullScreen={fullScreen} aria-labelledby="line-up-by-picture-title">
      <DialogTitle id="line-up-by-picture-title">Line up {angle.name} by picture</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 760 }}>
          Find one moment you can see in both, like a clap, a door closing or someone sitting down. Put each player
          on that frame, then choose Line up.
        </Typography>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <PicturePlayer
            label={`Master: ${master.name}`}
            url={master.url}
            durationMs={master.durationMs}
            positionMs={moments.masterMs}
            onPositionChange={(masterMs) => setMoments((previous) => ({ ...previous, masterMs }))}
            testId="picture-master"
          />
          <PicturePlayer
            label={angle.name}
            url={angle.url}
            durationMs={angle.durationMs}
            positionMs={moments.angleMs}
            onPositionChange={(angleMs) => setMoments((previous) => ({ ...previous, angleMs }))}
            testId="picture-angle"
          />
        </Box>
        <Alert severity={plan.problem ? 'warning' : 'info'} sx={{ mt: 3 }} data-testid="picture-line-up-preview" role="status">
          {plan.problem ?? describeStart(plan.offsetMs)}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onLineUp(plan)} disabled={Boolean(plan.problem)}>
          Line up
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LineUpByPictureDialog;
